<script setup lang="ts">
import Icon from '@components/Icon.vue';
import { OtherFiltersType } from '@enums/FilterType';
import { IconCallBell, IconDoor, IconLightbulb, IconPen, IconRoute, IconTrain } from '@icons';
import Accessibility from '@pages/search/components/filters/Accessibility.vue';
import ContractType from '@pages/search/components/filters/ContractType.vue';
import DropdownFilter from '@pages/search/components/filters/DropdownFilter.vue';
import Light from '@pages/search/components/filters/Light.vue';
import Services from '@pages/search/components/filters/Services.vue';
import Transports from '@pages/search/components/filters/Transports.vue';
import { trans } from '@tools/translation.ts';
import { ref } from 'vue';

const openDropdowns = ref<OtherFiltersType[]>([]);
const selectedFilters = ref<Record<string, string[]>>({
  [OtherFiltersType.CONTRACT_TYPE]: [],
  [OtherFiltersType.LIGHT]: [],
  [OtherFiltersType.SERVICES]: [],
  [OtherFiltersType.ACCESSIBILITY]: [],
  [OtherFiltersType.TRANSPORTS]: [],
});

const getSubtitleLabel = (code: string, type: OtherFiltersType): string => {
  if (type === OtherFiltersType.TRANSPORTS) {
    const [ , name, repetitionIndex ] = code.split('-');

    return `${trans('global.line')} ${name} ${repetitionIndex ?? ''}`;
  }

  return trans(`search.filters.other_filters.${type}.${code}`);
};

const getSubtitle = (type: OtherFiltersType): string => {
  const selected = selectedFilters.value[type];
  if (!selected || selected.length === 0) {
    return '';
  }

  return selected
    .map((code) => getSubtitleLabel(code, type))
    .join(', ');
};

const toggleDropdown = (type: OtherFiltersType): void => {
  const index = openDropdowns.value.indexOf(type);
  if (index > -1) {
    openDropdowns.value.splice(index, 1);
  } else {
    openDropdowns.value.push(type);
  }
};
const isDropdownOpen = (type: OtherFiltersType): boolean => openDropdowns.value.includes(type);
</script>

<template>
  <div class="other-filters-filter">
    <DropdownFilter
      :is-open="isDropdownOpen(OtherFiltersType.TRANSPORTS)"
      @click="toggleDropdown(OtherFiltersType.TRANSPORTS)"
    >
      <template #icon>
        <Icon
          :svg="IconTrain"
          width="24"
          height="24"
          fill="gray-dark"
        />
      </template>

      <template #text>
        {{ trans('search.filters.other_filters.transports.title') }}
      </template>

      <template v-if="getSubtitle(OtherFiltersType.TRANSPORTS)" #subtitle>
        {{ getSubtitle(OtherFiltersType.TRANSPORTS) }}
      </template>

      <template #panel>
        <Transports
          v-model:selected="selectedFilters[OtherFiltersType.TRANSPORTS]"
        />
      </template>
    </DropdownFilter>

    <DropdownFilter
      :is-open="isDropdownOpen(OtherFiltersType.IDEAL_LOCATION)"
      @click="toggleDropdown(OtherFiltersType.IDEAL_LOCATION)"
    >
      <template #icon>
        <Icon
          :svg="IconRoute"
          width="24"
          height="24"
          fill="gray-dark"
        />
      </template>

      <template #text>
        {{ trans('search.filters.other_filters.ideal_location.title') }}
      </template>

      <template #panel>
        <!-- https://bureaux-a-partager.atlassian.net/browse/MATCH-10659 -->
        @Todo
      </template>
    </DropdownFilter>

    <DropdownFilter
      :is-open="isDropdownOpen(OtherFiltersType.ACCESSIBILITY)"
      @click="toggleDropdown(OtherFiltersType.ACCESSIBILITY)"
    >
      <template #icon>
        <Icon
          :svg="IconDoor"
          width="24"
          height="24"
          fill="gray-dark"
        />
      </template>

      <template #text>
        {{ trans('search.filters.other_filters.accessibility.title') }}
      </template>

      <template v-if="getSubtitle(OtherFiltersType.ACCESSIBILITY)" #subtitle>
        {{ getSubtitle(OtherFiltersType.ACCESSIBILITY) }}
      </template>

      <template #panel>
        <Accessibility
          v-model:selected="selectedFilters[OtherFiltersType.ACCESSIBILITY]"
        />
      </template>
    </DropdownFilter>

    <DropdownFilter
      :is-open="isDropdownOpen(OtherFiltersType.SERVICES)"
      @click="toggleDropdown(OtherFiltersType.SERVICES)"
    >
      <template #icon>
        <Icon
          :svg="IconCallBell"
          width="24"
          height="24"
          fill="gray-dark"
        />
      </template>

      <template #text>
        {{ trans('search.filters.other_filters.services.title') }}
      </template>

      <template v-if="getSubtitle(OtherFiltersType.SERVICES)" #subtitle>
        {{ getSubtitle(OtherFiltersType.SERVICES) }}
      </template>

      <template #panel>
        <Services
          v-model:selected="selectedFilters[OtherFiltersType.SERVICES]"
        />
      </template>
    </DropdownFilter>

    <DropdownFilter
      :is-open="isDropdownOpen(OtherFiltersType.LIGHT)"
      @click="toggleDropdown(OtherFiltersType.LIGHT)"
    >
      <template #icon>
        <Icon
          :svg="IconLightbulb"
          width="24"
          height="24"
          fill="gray-dark"
        />
      </template>

      <template #text>
        {{ trans('search.filters.other_filters.light.title') }}
      </template>

      <template v-if="getSubtitle(OtherFiltersType.LIGHT)" #subtitle>
        {{ getSubtitle(OtherFiltersType.LIGHT) }}
      </template>

      <template #panel>
        <Light
          v-model:selected="selectedFilters[OtherFiltersType.LIGHT]"
        />
      </template>
    </DropdownFilter>

    <DropdownFilter
      :is-open="isDropdownOpen(OtherFiltersType.CONTRACT_TYPE)"
      @click="toggleDropdown(OtherFiltersType.CONTRACT_TYPE)"
    >
      <template #icon>
        <Icon
          :svg="IconPen"
          width="24"
          height="24"
          fill="gray-dark"
        />
      </template>

      <template #text>
        {{ trans('search.filters.other_filters.contract_type.title') }}
      </template>

      <template v-if="getSubtitle(OtherFiltersType.CONTRACT_TYPE)" #subtitle>
        {{ getSubtitle(OtherFiltersType.CONTRACT_TYPE) }}
      </template>

      <template #panel>
        <ContractType
          v-model:selected="selectedFilters[OtherFiltersType.CONTRACT_TYPE]"
        />
      </template>
    </DropdownFilter>
  </div>
</template>

<style lang="scss" scoped>
@use '@styles/mixins' as *;
@use '@styles/variables' as *;

@media screen and (min-width: $breakTablet) {
  .other-filters-filter {
    // 72 is the height of moreFilters footer which contains the CTA
    max-height: calc(100vh - ($headerSize + $criterionBarHeight + #{toRem(72)}));
  }
}
</style>
